import { t, Trans } from '@lingui/macro';
import hotkeys from 'hotkeys-js';
import { lazy, Suspense } from 'react';
import { IS_QA_ELIGIBLE } from '../../config/qaFeedback';

// 통역 스파이크 (dev 전용) — lazy: 운영 빌드에서 통역 스택(@google/genai)이 메인 번들에 실리지 않음
const InterpretationCallPanel = lazy(() => import('./InterpretationCallPanel'));
/** 참가자 타일의 언어 보조 표기 (통화 링크) — 얇게 유지하려 로컬 상수로 둔다 */
const ITP_LANG_SHORT: Record<string, string> = { ko: '한국어', ja: '日本語', en: 'English' };

/** 통화 링크 칩 모드 — 오디오 재생용 participant span 만 숨겨서 마운트 (비디오 타일 없이) */
function HiddenSpanHost({ html }: { html: HTMLElement }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => { ref.current?.appendChild(html); }, [html]);
  // ⚠ display:none(hidden) 금지 — 그 안의 <audio> 는 브라우저가 재생을 보류해
  // 통역 음성이 들리지 않는다(실측). 화면에서만 감추는 sr-only 로 마운트한다.
  return <div ref={ref} className="sr-only" aria-hidden />;
}
import { ChevronUp, Mic, MicOff, MonitorOff, MonitorUp, PhoneOff, Signal, SignalLow, SignalMedium, SignalZero, Users, Volume2 } from 'lucide-react';
import { useEffect, useReducer, useRef, useState } from 'react';
import { Room } from '../../models/Room';
import { My } from '../../services/My';
import { twil, TwilioHelpEvent } from '../../services/TwilioHelper';
import { itpCallIntent, itpParticipants } from '../../services/interpretation/hooks';
import { Util } from '../../services/Utils/Util';
import { UtilApp } from '../../services/Utils/UtilApp';
import { ParticipantItem, ScreenShareWindow } from './CallInfoItems';
import useDefaultDevices from './useDefaultDevices';

import { Badge } from "../../components/ui/badge";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { Button } from "../../components/ui/button";
import { useToast } from "../../components/ui/use-toast";
import { cn } from "../../lib/utils";
import { IconChevronDown } from './FigmaIcons';

hotkeys.unbind();

const CallHeader: React.FC = () => {
  const [_, forceUpdate] = useReducer((x) => x + 1, 0);
  const [expanded, setExpanded] = useState(false);
  const [roomName, setRoomName] = useState('');
  const [isOneToOne, setIsOneToOne] = useState(false);
  const [networkQualityLevel, setNetworkQualityLevel] = useState<number>(5);
  const { toast } = useToast();
  const { audioInputDevice, audioOutputDevice } = useDefaultDevices();
  const inputLabel = Util.getTextInsideParenthesis(audioInputDevice?.label);

  const [alertConfig, setAlertConfig] = useState<{
    open: boolean;
    title: React.ReactNode;
    description: React.ReactNode;
    onAction?: () => void;
  }>({ open: false, title: '', description: '' });

  useEffect(() => {
    let unsub1 = twil.subscribeToTwilioUpdates(twilioStateChangeHandler)
    // 통화 링크 참가자(이름·언어·음소거) 변경 반영 — 통역 스택을 import 하지 않는 얇은 레지스트리
    const unsubItp = itpParticipants.subscribe(() => forceUpdate());
    window.addEventListener('beforeunload', handleBeforeUnload);

    hotkeys('alt+a', function (event, handler) {
      event.preventDefault()
      if (twil.isConnected()) {
        toggleMic(!twil.isMicEnabled());
      }
    });

    hotkeys('alt+s', function (event, handler) {
      event.preventDefault()
      if (twil.isConnected()) {
        toggleScreenSharing();
      }
    });

    return () => { unsub1(); unsubItp(); twil.leaveRoom(); window.removeEventListener('beforeunload', handleBeforeUnload); hotkeys.unbind('alt+a'); hotkeys.unbind('alt+s'); };
  }, [])

  useEffect(() => {
    if (twil.audioInputLabel && inputLabel && inputLabel !== twil.audioInputLabel) {
      // twilio-video 2.28.x는 디바이스 자동 전환 지원 — 재연결 불필요
      twil.audioInputLabel = inputLabel;
      toastText(t`Microphone changed to ${inputLabel}.`);
    }
  }, [inputLabel])

  function showAlert(title: React.ReactNode, description: React.ReactNode, onAction?: () => void) {
    setAlertConfig({ open: true, title, description, onAction });
  }

  function twilioStateChangeHandler(e: TwilioHelpEvent | null) {
    if (e?.type === 'StateChange') {
      if (twil.isConnected()) {
        let room = My.Rooms.get(twil.getRoomId());
        if (room) {
          setRoomName(Room.getRoomName(room, My.user.id));
          setIsOneToOne(room.isOneToOne)
        }
      } else {
        setExpanded(false);
      }

      if (e?.error) {
        if (e.error === 'reconnecting') {
          toastText(t`Network connection is unstable. Reconnecting...`);
        } else if (e.error === 'reconnected') {
          toastText(t`Connection restored.`);
        } else if (e.error === 'disconnected') {
          toastText(t`Call connection lost.`);
        } else {
          const header = (e.error as any).header || t`Error`;
          const message = (e.error as any).message || JSON.stringify(e.error);
          showAlert(header, message);
        }
      }
      forceUpdate();
    } else if (e?.type === 'NetworkQuality') {
      const level = Number(e.error);
      setNetworkQualityLevel(level);
      if (level <= 1) {
        toastText(t`Network condition is poor. Audio may be interrupted.`);
      }
    } else if (e?.type === 'ScreenShareChanged') {
      // 화면공유 시작/종료 시 floating 창 표시/제거 위해 재렌더 (별도 창이라 패널 펼침 불필요)
      forceUpdate();
    }
  }

  async function handleBeforeUnload(e: BeforeUnloadEvent) {
    if (twil.isConnected()) {
      e.preventDefault();
      console.log('### beforeunload handled');
      twil.leaveRoom();
      e.returnValue = "Are you sure you want to leave?";
    }
  }

  return (
    <>
      {/* 원격 화면공유 floating 창 (자체 portal 렌더) */}
      {twil.isConnected() && twil.getScreenSharerId() && twil.screenShareSpan && (
        <ScreenShareWindow
          key={twil.getScreenSharerId()!}
          span={twil.screenShareSpan}
          sharerId={twil.getScreenSharerId()!}
        />
      )}

      <div className="fixed top-0 left-0 right-0 z-50 pointer-events-none flex flex-col items-center">
        {twil.isConnecting && (
          <div className="w-full h-1 bg-primary/20">
            <div className="h-full bg-primary w-1/3 animate-indeterminate-slide rounded-full" />
          </div>
        )}

        {twil.isConnected() && (
          <div className={cn(
            "pointer-events-auto transition-all duration-300 ease-in-out shadow-lg mx-4 mt-2",
            "bg-[rgb(var(--panel-dark))] backdrop-blur-md text-[rgb(var(--panel-dark-text))] rounded-2xl overflow-hidden border border-[var(--panel-dark-border)]",
            expanded ? "max-w-2xl w-full" : "max-w-fit"
          )}>
            {/* Header / Toggle Row */}
            <div
              className="flex items-center justify-between px-4 py-2 gap-4 cursor-pointer hover:bg-[var(--panel-dark-hover)] transition-colors"
              onClick={() => setExpanded(p => !p)}
            >
              <div className="flex items-center gap-2">
                {expanded ? <ChevronUp className="w-5 h-5 text-[var(--panel-dark-text-muted)]" /> : <IconChevronDown className="w-5 h-5 text-[var(--panel-dark-text-muted)]" />}
                <div className="flex items-center gap-3 ml-1">
                  <span className="font-semibold text-lg max-w-[150px] sm:max-w-[200px] truncate leading-none" title={roomName}>
                    #{roomName}
                  </span>
                  <Badge variant="secondary" className="flex items-center gap-1.5 px-2 py-0.5 bg-[var(--panel-dark-muted)] text-[rgb(var(--panel-dark-text))] hover:bg-[var(--panel-dark-muted)] border-0 h-6 shrink-0">
                    <Users className="w-3 h-3" />
                    <span className="text-xs font-medium tabular-nums">{twil.participantSpans.size}</span>
                  </Badge>
                  <NetworkQualityIndicator level={networkQualityLevel} />
                </div>
              </div>

              {/* Quick Actions (Always visible) */}
              <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                {UtilApp.getPlatform() === 'web' && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-[rgb(var(--panel-dark-text))] hover:bg-[var(--panel-dark-muted)] hover:text-[rgb(var(--panel-dark-text))]"
                    onClick={toggleScreenSharing}
                    title={twil.isScreenSharing() ? t`Stop sharing` : t`Share screen`}
                  >
                    {twil.isScreenSharing() ? <MonitorOff className="w-5 h-5" /> : <MonitorUp className="w-5 h-5" />}
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-[rgb(var(--panel-dark-text))] hover:bg-[var(--panel-dark-muted)] hover:text-[rgb(var(--panel-dark-text))]"
                  onClick={() => toggleMic(!twil.isMicEnabled())}
                  title={twil.isMicEnabled() ? t`Mute` : t`Unmute`}
                >
                  {twil.isMicEnabled() ? <Mic className="w-5 h-5" /> : <MicOff className="w-5 h-5 text-destructive" />}
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-destructive hover:bg-destructive hover:text-destructive-foreground"
                  onClick={() => {
                    // 명시적 종료 — 새로고침·이탈과 구분(통화 링크면 링크까지 폐기)
                    itpCallIntent.userEnded = true;
                    twil.leaveRoom();
                  }}
                  data-cy="close-call"
                  title={t`Leave call`}
                >
                  <PhoneOff className="w-5 h-5" />
                </Button>
              </div>
            </div>

            {/* Expanded Content */}
            <div className={cn("px-4 pb-4 pt-2 border-t border-[var(--panel-dark-border)] space-y-4", !expanded && "hidden")}>
              {/* 통역 (Gate 3 스파이크, dev 전용 — IS_QA_ELIGIBLE 로 운영 DCE) */}
              {IS_QA_ELIGIBLE && (
                <Suspense fallback={null}><InterpretationCallPanel /></Suspense>
              )}
              {/* Participants — 채널 통화는 비디오 타일. 통화 링크(itp 레지스트리에 참가자가
                  있을 때)는 카메라 없는 통화라 까만 비디오 타일 대신 게스트 화면과 같은
                  인라인 칩(이니셜·이름·언어·마이크)으로 표시한다. 오디오는 span 안의
                  <audio> 가 재생하므로 span 은 숨김 마운트로 계속 살려둔다 */}
              {itpParticipants.map.size > 0 ? (
                <>
                  <div className="flex flex-wrap items-center gap-1.5">
                    {Array.from(itpParticipants.map.entries()).map(([id, p]) => (
                      <span key={id}
                        className="inline-flex max-w-full items-center gap-1.5 rounded-full bg-[var(--panel-dark-muted)] py-1 pl-1 pr-2.5">
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[var(--panel-dark-hover)] text-meta font-semibold">
                          {p.name.trim().charAt(0) || '?'}
                        </span>
                        <span className="truncate text-label font-medium">{p.name}</span>
                        {p.lang && (
                          <span className="shrink-0 text-meta text-[var(--panel-dark-text-muted)]">
                            {ITP_LANG_SHORT[p.lang] ?? p.lang}
                          </span>
                        )}
                        {p.muted
                          ? <MicOff className="h-3.5 w-3.5 shrink-0 text-destructive" />
                          : <Mic className="h-3.5 w-3.5 shrink-0 text-[var(--panel-dark-text-muted)]" />}
                      </span>
                    ))}
                  </div>
                  {Array.from(twil.participantSpans.values()).map((span) => (
                    <HiddenSpanHost key={span.id} html={span} />
                  ))}
                </>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {Array.from(twil.participantSpans.values()).map((span) => (
                    <div key={span.id} className="bg-[var(--panel-dark-muted)] rounded px-2 py-1">
                      <ParticipantItem html={span} />
                    </div>
                  ))}
                </div>
              )}

              {/* Screen Sharing Info */}
              {twil.isScreenSharing() && (
                <div className="flex items-center justify-between bg-blue-500/20 rounded-lg p-3 border border-blue-500/30">
                  <div className="flex items-center gap-2 text-sm">
                    <MonitorUp className="w-4 h-4 text-blue-400" />
                    <span><Trans>You are now presenting your screen.</Trans></span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={toggleScreenSharing}
                    className="text-destructive hover:bg-destructive hover:text-destructive-foreground h-8"
                  >
                    <Trans>Stop screensharing</Trans>
                  </Button>
                </div>
              )}

              {/* Device Info */}
              <div className="flex flex-col gap-1 text-xs text-muted-foreground">
                {inputLabel && (
                  <div className="flex items-center gap-2">
                    <Mic className="w-3 h-3" />
                    <span>{inputLabel}</span>
                  </div>
                )}
                {audioOutputDevice && (
                  <div className="flex items-center gap-2">
                    <Volume2 className="w-3 h-3" />
                    <span>{audioOutputDevice?.label.replace('Default - ', '')}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <AlertDialog open={alertConfig.open} onOpenChange={(open) => setAlertConfig(p => ({ ...p, open }))}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{alertConfig.title}</AlertDialogTitle>
            <AlertDialogDescription>{alertConfig.description}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => {
              if (alertConfig.onAction) alertConfig.onAction();
              setAlertConfig(p => ({ ...p, open: false }));
            }}>
              {t`OK`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );


  async function toggleScreenSharing() {
    if (!twil.isScreenSharing()) {
      let res = await twil.startScreenSharing(forceUpdate);
      if (res == 'busy') {
        showAlert(t`Screen Sharing`, t`Someone is already sharing the screen. They must stop sharing before you can start sharing your screen.`);
      } else if (res == 'failed') {
        showAlert(t`Error`, t`Failed to share your screen.`);
      } else if (res == 'started') {
        toastText(t`Sharing your screen (keyboard shortcut: Alt + s)`);
      }
    } else {
      twil.stopScreenSharing();
    }
    forceUpdate();
  };


  function toggleMic(value: boolean) {
    twil.enableLocalMicrophone(value);
    let msg = value ? t`Microphone is now enabled` : t`Microphone is now disabled`;
    if (UtilApp.getPlatform() === 'web') {
      msg += ' ' + t`(keyboard shortcut: Alt + a)`;
    }
    toastText(msg);
  }

  function toastText(text: string) {
    toast({
      description: text,
      
    });
  }
}

export default CallHeader;

/** 네트워크 품질 인디케이터 (Twilio level 0~5) */
function NetworkQualityIndicator({ level }: { level: number }) {
  const config = (() => {
    if (level <= 1) return { Icon: SignalZero, color: 'text-destructive', label: t`Very Poor` };
    if (level === 2) return { Icon: SignalLow, color: 'text-primary', label: t`Poor` };
    if (level === 3) return { Icon: SignalMedium, color: 'text-yellow-400', label: t`Fair` };
    return { Icon: Signal, color: 'text-emerald-400', label: t`Good` };
  })();

  return (
    <div className={cn("flex items-center", config.color)} title={config.label}>
      <config.Icon className="w-3.5 h-3.5" />
    </div>
  );
}
